import { Radio } from '@arco-design/web-react';
import { useTranslation } from 'react-i18next';
import { useFilters } from '@/context/FilterContext';
import type { PeriodPreset } from '@/types/Filters';

const PRESETS: PeriodPreset[] = ['all', 'current-month', 'quarter', 'calendar-year', 'custom'];

function toInputValue(d: Date | undefined): string {
  if (!d) return '';
  // <input type="date"> usa YYYY-MM-DD em horario local.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fromInputValue(s: string): Date | null {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function PeriodFilter() {
  const { filters, dispatch } = useFilters();
  const { t } = useTranslation();
  const { period } = filters;

  const onPresetChange = (preset: PeriodPreset) => {
    dispatch({ type: 'SET_PERIOD', payload: { preset } });
  };

  const onCustomChange = (which: 'from' | 'to', value: string) => {
    const parsed = fromInputValue(value);
    if (!parsed) return;
    const current = period.customRange ?? {
      from: new Date(2026, 2, 16),
      to: new Date(2027, 2, 15),
    };
    const next = which === 'from' ? { ...current, from: parsed } : { ...current, to: parsed };
    dispatch({ type: 'SET_PERIOD', payload: { preset: 'custom', customRange: next } });
  };

  return (
    <div className="aiobi-filter aiobi-filter--period">
      <label className="aiobi-filter__label">{t('filters.period.label')}</label>
      <Radio.Group
        type="button"
        value={period.preset}
        onChange={(v) => onPresetChange(v as PeriodPreset)}
        size="small"
      >
        {PRESETS.map((p) => (
          <Radio key={p} value={p}>
            {t(`filters.period.preset.${p}`)}
          </Radio>
        ))}
      </Radio.Group>
      {period.preset === 'custom' && (
        <div className="aiobi-filter__custom-range">
          <input
            type="date"
            value={toInputValue(period.customRange?.from)}
            onChange={(e) => onCustomChange('from', e.target.value)}
            aria-label={t('filters.period.from')}
          />
          <span aria-hidden="true">→</span>
          <input
            type="date"
            value={toInputValue(period.customRange?.to)}
            onChange={(e) => onCustomChange('to', e.target.value)}
            aria-label={t('filters.period.to')}
          />
        </div>
      )}
    </div>
  );
}
